/**
 * Packaging OS — router (Phase 1R-A-Fix-2: locale-prefixed routes).
 *
 * Each public surface is mounted twice:
 *   1. The original non-prefixed path (`/login`, `/app`, …) — preserved
 *      verbatim for backwards compatibility with existing links.
 *   2. The locale-prefixed path (`/:locale/login`, `/:locale/app`, …)
 *      under <LocaleSync> which validates `:locale` against the four
 *      supported codes (en/ar/fa/tr) and syncs the locale state on
 *      navigation. Unknown prefixes fall through to NotFound.
 *
 * Deep links from a Core post-OAuth redirect (e.g.
 * `/fa/login?postauth=1`) therefore land on the LoginPage with the
 * correct locale active, instead of the NotFound page.
 */

import { Route, Routes } from "react-router-dom";
import { AppShellPage } from "../pages/AppShellPage";
import { BrandsPage } from "../pages/BrandsPage";
import { LoginPage } from "../pages/LoginPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { PackagingAuditWorkflowPage } from "../pages/PackagingAuditWorkflowPage";
import { PackagingProjectsPage } from "../pages/PackagingProjectsPage";
import { PreviewPage } from "../pages/PreviewPage";
import { LabelCopyPage } from "../pages/LabelCopyPage";
import { LaunchKitPage } from "../pages/LaunchKitPage";
import { MaterialStructurePage } from "../pages/MaterialStructurePage";
import { PackagingScorecardPage } from "../pages/PackagingScorecardPage";
import { PostLaunchAnalysisPage } from "../pages/PostLaunchAnalysisPage";
import { ProductBriefPage } from "../pages/ProductBriefPage";
import { ProductsPage } from "../pages/ProductsPage";
import { PublicLanding } from "../pages/PublicLanding";
import { ServicesPage } from "../pages/ServicesPage";
import { RequestAccessPage } from "../pages/RequestAccessPage";
import { AdminConsolePage } from "../pages/AdminConsolePage";
import { ArtifactsPage } from "../pages/ArtifactsPage";
import { KnowledgePage } from "../pages/KnowledgePage";
import { ProspectsPage } from "../pages/ProspectsPage";
import { SupplierDirectoryPage } from "../pages/SupplierDirectoryPage";
import { ProjectStudioPage } from "../pages/ProjectStudioPage";
import { ReviewChecklistPage } from "../pages/ReviewChecklistPage";
import { SupplierRfqPage } from "../pages/SupplierRfqPage";
import { SustainabilityRiskPage } from "../pages/SustainabilityRiskPage";
import { VisualDirectionPage } from "../pages/VisualDirectionPage";
import { LocaleSync } from "./LocaleSync";

export function AppRouter(): JSX.Element {
  return (
    <Routes>
      {/* Non-prefixed routes (backwards compatibility) */}
      <Route path="/" element={<PublicLanding />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/request" element={<RequestAccessPage />} />
      <Route path="/app/admin" element={<AdminConsolePage />} />
      <Route path="/app/artifacts" element={<ArtifactsPage />} />
      <Route path="/app/knowledge" element={<KnowledgePage />} />
      <Route path="/app/prospects" element={<ProspectsPage />} />
      <Route path="/app/suppliers" element={<SupplierDirectoryPage />} />
      <Route path="/app/projects" element={<ProjectStudioPage />} />
      <Route path="/app/projects/:projectId" element={<ProjectStudioPage />} />
      <Route path="/app/projects/:projectId/:tab" element={<ProjectStudioPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/preview" element={<PreviewPage />} />
      <Route path="/app" element={<AppShellPage />} />
      <Route path="/app/catalog/brands" element={<BrandsPage />} />
      <Route path="/app/catalog/products" element={<ProductsPage />} />
      <Route
        path="/app/catalog/packaging-projects"
        element={<PackagingProjectsPage />}
      />
      <Route
        path="/app/workflows/packaging-audit"
        element={<PackagingAuditWorkflowPage />}
      />
      <Route
        path="/app/workflows/product-brief"
        element={<ProductBriefPage />}
      />
      <Route
        path="/app/workflows/label-copy"
        element={<LabelCopyPage />}
      />
      <Route
        path="/app/workflows/material-structure"
        element={<MaterialStructurePage />}
      />
      <Route
        path="/app/workflows/sustainability-risk"
        element={<SustainabilityRiskPage />}
      />
      <Route
        path="/app/workflows/supplier-rfq"
        element={<SupplierRfqPage />}
      />
      <Route
        path="/app/workflows/visual-direction"
        element={<VisualDirectionPage />}
      />
      <Route
        path="/app/workflows/packaging-scorecard"
        element={<PackagingScorecardPage />}
      />
      <Route
        path="/app/workflows/launch-kit"
        element={<LaunchKitPage />}
      />
      <Route
        path="/app/workflows/post-launch-analysis"
        element={<PostLaunchAnalysisPage />}
      />
      <Route
        path="/app/workflows/review-checklist"
        element={<ReviewChecklistPage />}
      />

      {/* Locale-prefixed routes — same surface, locale state synced */}
      <Route path=":locale" element={<LocaleSync />}>
        <Route index element={<PublicLanding />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="request" element={<RequestAccessPage />} />
        <Route path="app/admin" element={<AdminConsolePage />} />
        <Route path="app/artifacts" element={<ArtifactsPage />} />
        <Route path="app/knowledge" element={<KnowledgePage />} />
        <Route path="app/prospects" element={<ProspectsPage />} />
        <Route path="app/suppliers" element={<SupplierDirectoryPage />} />
        <Route path="app/projects" element={<ProjectStudioPage />} />
        <Route path="app/projects/:projectId" element={<ProjectStudioPage />} />
        <Route path="app/projects/:projectId/:tab" element={<ProjectStudioPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="preview" element={<PreviewPage />} />
        <Route path="app" element={<AppShellPage />} />
        <Route path="app/catalog/brands" element={<BrandsPage />} />
        <Route path="app/catalog/products" element={<ProductsPage />} />
        <Route
          path="app/catalog/packaging-projects"
          element={<PackagingProjectsPage />}
        />
        <Route
          path="app/workflows/packaging-audit"
          element={<PackagingAuditWorkflowPage />}
        />
        <Route
          path="app/workflows/product-brief"
          element={<ProductBriefPage />}
        />
        <Route
          path="app/workflows/label-copy"
          element={<LabelCopyPage />}
        />
        <Route
          path="app/workflows/material-structure"
          element={<MaterialStructurePage />}
        />
        <Route
          path="app/workflows/sustainability-risk"
          element={<SustainabilityRiskPage />}
        />
        <Route
          path="app/workflows/supplier-rfq"
          element={<SupplierRfqPage />}
        />
        <Route
          path="app/workflows/visual-direction"
          element={<VisualDirectionPage />}
        />
        <Route
          path="app/workflows/packaging-scorecard"
          element={<PackagingScorecardPage />}
        />
        <Route
          path="app/workflows/launch-kit"
          element={<LaunchKitPage />}
        />
        <Route
          path="app/workflows/post-launch-analysis"
          element={<PostLaunchAnalysisPage />}
        />
        <Route
          path="app/workflows/review-checklist"
          element={<ReviewChecklistPage />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
